import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Select from 'react-select';
import { useDebouncedCallback } from 'use-debounce';
import { useChangeAwareEffect } from 'use-change-aware-effect';
import FeaturedWiresContext from './context';
import selectorProps from './selector-style';
import WireCandidate from './wire-candidate';
import {
  useGetFeatureableWires, usePostFeaturedWires,
} from '../../../../../services/wires/featured-wires-service';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  ${props => props.hasAnchor && `
    margin-top: -16px
  `};
`;

const Anchor = styled.a`
  align-self: flex-end;
  display: inline-block;
  font: ${props => props.theme.fontWeightBold} 14px/120% ${props => props.theme.fontFamilyBody};
  margin-bottom: 8px;
  color: ${props => props.theme.secondaryColor};
  text-transform: capitalize;
`;

export default function Selector({ prevId }) {
  const [, search] = useGetFeatureableWires();
  const { callback: debouncedSearch } = useDebouncedCallback(search, 300, { leading: true });
  const [, postFeatured] = usePostFeaturedWires();
  const { state, dispatch } = React.useContext(FeaturedWiresContext);
  const [options, setOptions] = React.useState(state.initial);
  const mounted = React.useRef(false);
  const selectRef = React.useRef();
  const valueChanged = React.useRef(false);

  useChangeAwareEffect(({ isMount }) => {
    if (!isMount) {
      return () => null;
    }
    mounted.current = true;
    prevId && _.get(selectRef, 'current') && selectRef.current.focus();
    return () => (mounted.current = false);
  }, { prevId });

  React.useEffect(() => {
    state.initial.length && setOptions(state.initial);
  }, [state.initial]);

  const undo = e => {
    e && e.preventDefault();
    mounted.current && prevId && dispatch({ type: 'updateWire', payload: { id: prevId, toChange: false } });
  };

  const onInputChange = v => {
    if (mounted.current) {
      debouncedSearch(v || '').then(({ data }) => {
        const wires = _.get(data, 'wires', []);
        if (wires.length) {
          setOptions(wires.map(w => ({ ...w, label: w.title, value: w.id })));
        }
      });
    }
  };

  const onChange = v => {
    valueChanged.current = true;
    if (mounted.current) {
      if (v.id === prevId) {
        undo();
      } else {
        postFeatured(v.id, prevId).then(() => {
          const action = prevId
            ? { type: 'replaceWire', payload: { prevId, wire: v } }
            : { type: 'addWire', payload: v };
          dispatch(action);
        });
      }
    }
  };

  const onMenuClose = () => {
    // This event is always happening before onChange.
    setTimeout(() => (mounted.current && valueChanged.current || undo()), 50);
  };

  const isDisabled = d => prevId !== d.id && _.find(state.featured, { id: d.id });
  const isSelected = d => prevId === d.id;

  return (
    <Container hasAnchor={!!prevId}>
      {!!prevId && <Anchor href="#" onClick={undo}>undo</Anchor>}
      <Select
        {...selectorProps}
        ref={selectRef}
        options={options}
        className="featured-wires-select--container"
        onInputChange={onInputChange}
        onChange={onChange}
        onMenuClose={onMenuClose}
        formatOptionLabel={WireCandidate}
        isOptionDisabled={isDisabled}
        isOptionSelected={isSelected}
      />
    </Container>
  );
}

Selector.propTypes = {
  prevId: PropTypes.number,
};

Selector.defaultProps = { prevId: null };
